import type { OrderingBasketAddon, OrderingBasketLine } from '@/domains/pjazza/types/models/OrderingBasket.types.ts'
import type { OrderingPrice } from '@/domains/pjazza/types/models/OrderingMenu.types.ts'
import type { MoneyModel } from '@/domains/pricing/types/models/MoneyModel.types.ts'

import { fromMinor, multiply, safeSum, toMinor } from '@/utils/money-util/moneyUtil'

interface PricedQuantity {
  price: OrderingPrice
  quantity: number
}

const lineMoney = (item: PricedQuantity, addons: PricedQuantity[]): MoneyModel =>
  safeSum({
    moneyArray: [item, ...addons].map(({ price, quantity }) =>
      multiply({ money: fromMinor({ minorAmount: price.amount, currencyCode: price.currency }), multiplier: quantity })
    ),
    currencyCode: item.price.currency
  })

/** An option's total on a line. One that follows the line takes its quantity now, held to its maximum. */
export const addonLineQuantity = (addon: OrderingBasketAddon, lineQuantity: number): number =>
  addon.quantity ?? Math.min(lineQuantity, addon.maxQuantity ?? lineQuantity)

const basketLineMoney = (line: OrderingBasketLine): MoneyModel =>
  lineMoney(
    { price: line.unitPrice, quantity: line.quantity },
    line.addons.map(addon => ({ price: addon.price, quantity: addonLineQuantity(addon, line.quantity) }))
  )

/**
 * A line's display total in minor units: the item at its quantity, plus each
 * option at its own quantity. The server's quote is what gets charged.
 */
export const pricedLineTotal = (item: PricedQuantity, addons: PricedQuantity[]): number =>
  toMinor({ money: lineMoney(item, addons) })

export const basketLineTotal = (line: OrderingBasketLine): number => toMinor({ money: basketLineMoney(line) })

export const basketTotal = (lines: OrderingBasketLine[], currency: string): number =>
  toMinor({ money: safeSum({ moneyArray: lines.map(basketLineMoney), currencyCode: currency }) })
